import { db } from '../firebase';
import { collection, writeBatch, doc, Timestamp } from 'firebase/firestore';
import { AnalyticsEvents } from './analytics';

const SEED_DAYS = 30;
const EVENTS_PER_DAY = 50;

/**
 * Generates realistic historic data for the last 30 days
 */
export const seedHistoricAnalytics = async (onProgress) => {
  const telemetryRef = collection(db, 'telemetry');
  const now = new Date();
  
  console.log("Starting to seed historic analytics data...");

  const platforms = ['iOS', 'Android', 'Web'];
  const countries = ['USA', 'UK', 'Canada', 'Germany', 'India', 'Australia'];
  const sources = ['Organic', 'Social', 'Referral', 'Ads', 'Direct'];
  
  const episodes = [
    { id: 'ep1', title: 'Startup Talks #42', podcastId: 'p1' },
    { id: 'ep2', title: 'Crime Stories: The Vault', podcastId: 'p2' },
    { id: 'ep3', title: 'Daily News Brief', podcastId: 'p3' },
  ];

  const bots = [
    { id: 'bot-1', ua: 'Googlebot/2.1 (+http://www.google.com/bot.html)' },
    { id: 'bot-2', ua: 'Mozilla/5.0 (compatible; SemrushBot/7~bl; +http://www.semrush.com/bot.html)' },
    { id: 'malicious-1', ua: 'HeadlessChrome/91.0.4472.101' },
    { id: 'farmer-1', ua: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.114 Safari/537.36' }
  ];

  for (let i = 0; i < SEED_DAYS; i++) {
    const dayDate = new Date(now);
    dayDate.setDate(now.getDate() - i);

    const batch = writeBatch(db);
    if (onProgress) onProgress(`Day ${i+1}/${SEED_DAYS}`);

    for (let j = 0; j < EVENTS_PER_DAY; j++) {
      const eventDate = new Date(dayDate);
      eventDate.setHours(Math.floor(Math.random() * 24));
      eventDate.setMinutes(Math.floor(Math.random() * 60));

      const eventType = getRandomEvent();
      const properties = generateProperties(eventType, platforms, countries, sources, episodes);

      const dRef = doc(telemetryRef);
      batch.set(dRef, {
        event: eventType,
        ...properties,
        timestamp: Timestamp.fromDate(eventDate),
        isMock: true 
      });
    }

    // Occasionally seed a "Burst" bot
    if (Math.random() > 0.8) {
      const bot = bots[Math.floor(Math.random() * bots.length)];
      for (let k = 0; k < 30; k++) {
        const burstDate = new Date(dayDate);
        burstDate.setMinutes(k % 5);
        const dRef = doc(telemetryRef);
        batch.set(dRef, {
          event: AnalyticsEvents.PAGE_VIEW,
          userId: bot.id,
          userAgent: bot.ua,
          timestamp: Timestamp.fromDate(burstDate),
          isMock: true
        });
      }
    }

    // Seed a "Stream Farmer" occasionally
    if (Math.random() > 0.9) {
      const farmer = bots.find(b => b.id === 'farmer-1');
      const targetEp = episodes[0];
      for (let k = 0; k < 50; k++) {
        const farmDate = new Date(dayDate);
        farmDate.setMinutes(k % 60);
        const dRef = doc(telemetryRef);
        batch.set(dRef, {
          event: AnalyticsEvents.PLAY_START,
          userId: farmer.id,
          userAgent: farmer.ua,
          podcastId: targetEp.podcastId,
          episodeTitle: targetEp.title,
          timestamp: Timestamp.fromDate(farmDate),
          isMock: true
        });
      }
    }
    
    await batch.commit();
    console.log(`Seeded day ${i+1}/${SEED_DAYS}`);
  }

  console.log("Seeding complete!");
};

function getRandomEvent() {
  const events = [
    { type: AnalyticsEvents.USER_LOGIN, weight: 10 },
    { type: AnalyticsEvents.USER_SIGNUP, weight: 2 },
    { type: AnalyticsEvents.PLAY_START, weight: 30 },
    { type: AnalyticsEvents.PLAY_COMPLETE, weight: 15 },
    { type: AnalyticsEvents.PAGE_VIEW, weight: 25 },
    { type: AnalyticsEvents.SEARCH_PERFORMED, weight: 15 },
    { type: AnalyticsEvents.PODCAST_FOLLOW, weight: 3 },
  ];

  const totalWeight = events.reduce((sum, e) => sum + e.weight, 0);
  let random = Math.random() * totalWeight;

  for (const event of events) {
    if (random < event.weight) return event.type;
    random -= event.weight;
  }
  return AnalyticsEvents.PAGE_VIEW;
}

function generateProperties(eventType, platforms, countries, sources, episodes) {
  const common = {
    platform: platforms[Math.floor(Math.random() * platforms.length)],
    country: countries[Math.floor(Math.random() * countries.length)],
  };

  switch (eventType) {
    case AnalyticsEvents.USER_SIGNUP:
    case AnalyticsEvents.USER_LOGIN:
      return { ...common, source: sources[Math.floor(Math.random() * sources.length)] };
    
    case AnalyticsEvents.PLAY_START:
    case AnalyticsEvents.PLAY_COMPLETE:
      const ep = episodes[Math.floor(Math.random() * episodes.length)];
      return { ...common, ...ep, duration: Math.floor(Math.random() * 3600) + 600 };

    case AnalyticsEvents.SEARCH_PERFORMED:
      return { ...common, query: 'podcasts' };

    default:
      return common;
  }
}
